import { fetchOrderItemFlightDetails } from 'actions/OrderActions'
import { ITEM_STATUS_CANCELLED } from 'constants/cart'
import { CHARTER } from 'constants/provider'
import { getAddedBaggageFromAddons, getPendingBaggageFromAddons } from './flightUtils'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineUtensilsIcon from 'LuxKit/Icons/line/LineUtensilsIcon'
import { allCountries } from 'country-telephone-data'
import type { AppDispatch } from 'src/client/store'
import { nonNullable } from 'lib/array/arrayUtils'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

// Fetch flight details for multiple flight items in order
export const fetchMultipleFlightDetails = (
  dispatch: AppDispatch,
  orderId: string,
  flightItems: Array<App.OrderFlightItem>,
) => {
  flightItems.forEach(item => {
    dispatch(fetchOrderItemFlightDetails(orderId, item.id))
  })
}

// Get all flight details from Redux state
export const getAllFlightDetails = (
  flightItems: Array<App.OrderFlightItem>,
  flightDetailsState: any,
): Array<App.FlightOrderItemDetails & { flightItemId: string }> => {
  return nonNullable(flightItems.map(item => {
    const flightDetails = flightDetailsState[item.id]?.flight
    if (!flightDetails) {
      return undefined
    }
    return {
      ...flightDetails,
      flightItemId: item.id,
    }
  }))
}

// Combine all legs from multiple flight items
export const combineAllFlightLegs = (
  allFlightDetails: Array<App.FlightOrderItemDetails & { flightItemId: string }>,
): Array<App.LegWithFlightItemId> => {
  return allFlightDetails.flatMap(flightDetails => {
    return flightDetails.bookingJourney?.legs?.map((leg) => {
      return {
        ...leg,
        flightItemId: flightDetails.flightItemId,
      }
    }) ?? []
  })
}

/**
 * A leg's route as the customer reads it: where it starts to where it ends, e.g. "MEL - MLE".
 *
 * Shared because this string is persisted onto a baggage selection (`legLabel`) and shown back at
 * checkout, so the My Escapes entry point and the checkout review have to word it identically.
 */
export function flightLegRouteLabel(flights: Array<App.LegFlight> | undefined): string {
  const first = flights?.[0]
  const last = flights?.[flights.length - 1]
  return first && last ? `${first.departureAirport} - ${last.arrivalAirport}` : ''
}

/**
 * Maps each leg to its position within its OWN flight item's `bookingJourney.legs`.
 *
 * `combineAllFlightLegs` concatenates every flight item's legs into one array, so a leg's
 * position in that array is NOT its position in its own booking journey once an order has
 * more than one flight item. svc-flights resolves a baggage addon back through
 * `booking_journey.legs[legIndex].id`, so using the concatenated position would attach
 * baggage to the wrong leg, or be rejected outright.
 */
export function buildLegIndexByLeg(
  allLegs: Array<App.LegWithFlightItemId>,
): Map<App.LegWithFlightItemId, number> {
  const indexes = new Map<App.LegWithFlightItemId, number>()
  const nextIndexByItem = new Map<string, number>()

  for (const leg of allLegs) {
    const next = nextIndexByItem.get(leg.flightItemId) ?? 0
    indexes.set(leg, next)
    nextIndexByItem.set(leg.flightItemId, next + 1)
  }

  return indexes
}

// Check if any flight details from order are still loading
export const areFlightDetailsLoading = (
  flightItems: Array<App.OrderFlightItem>,
  flightDetailsState: any,
): boolean => flightItems.some(item => {
  const details = flightDetailsState[item.id]
  return Boolean(details?.fetching && !details?.flight)
})

/**
 * The baggage this traveller bought on top of their fare, as rows for the traveller card.
 *
 * A bag bought after booking is provider-booked asynchronously, so for a short window — including
 * the moment the customer lands on the confirmation page — it is paid for but not yet confirmed.
 * Those are listed too, with their own wording: hiding them would read as if the purchase had not
 * happened, and calling them purchased would promise something the airline has not agreed to.
 */
export const travellerAddedBaggage = (
  traveller: App.FlightTraveller,
  selectedFlight: App.LegFlightWithAddons,
  intl: IntlShape,
) => {
  const travellerAddons = selectedFlight.addons.filter(addon => addon.travellerId === traveller.id)

  return [
    ...formatAddedBaggage(getAddedBaggageFromAddons(travellerAddons), intl),
    ...formatPendingBaggage(getPendingBaggageFromAddons(travellerAddons), intl),
  ]
}

export function getTravellerFlightInclusions(
  selectedFlight: App.LegFlightWithAddons,
  traveller: App.FlightTraveller,
): App.LegFlightWithAddons['inclusions'] {
  const travellerIds = [
    traveller.id,
    traveller.provider_reference,
    traveller.mapped_traveler,
  ].filter(Boolean)

  const travellerFareDetails = selectedFlight.travellerFareDetails?.find(fareDetails => travellerIds.includes(fareDetails.travellerId))

  return travellerFareDetails?.inclusions ?? selectedFlight.inclusions
}

export function getTravellerFlightCabinLabel(
  selectedFlight: App.LegFlightWithAddons,
  traveller: App.FlightTraveller,
): string | undefined {
  if (!selectedFlight.hasMixedCabinClassLabels) {
    return
  }

  const travellerIds = [
    traveller.id,
    traveller.provider_reference,
    traveller.mapped_traveler,
  ].filter(Boolean)

  const travellerFareDetails = selectedFlight.travellerFareDetails?.find(fareDetails => travellerIds.includes(fareDetails.travellerId))

  return travellerFareDetails?.cabinClassLabel ?? selectedFlight.baseCabinLabel
}

const messages = defineMessages({
  purchasedBaggage: {
    id: 'XbAYcopr24Hb-7JBCeD5',
    defaultMessage: '{quantity}x {description} (purchased)',
    description: 'Included feature label for purchased checked baggage quantity and description',
  },
  pendingBaggage: {
    id: '3xhOhqk7E9N2ShttN9t4',
    defaultMessage: '{quantity}x {description} (being confirmed)',
    description: 'Label for checked baggage the customer has paid for but whose airline booking has not come back yet, shown on the My Escapes flight traveller card',
  },
  notIncluded: {
    id: 'N3Djv-YtPplfZGFJnTeO',
    defaultMessage: 'Not included',
    description: 'Default label for an item that is not included in flight inclusions',
  },
  seatNotSelected: {
    id: 'CgTWQG8RmkWSSvKqk_Ew',
    defaultMessage: 'Seat not selected',
    description: 'Text shown when no seat has been selected for a flight',
  },
})

const formatMessage = (
  intl: IntlShape,
  descriptor: MessageDescriptor,
  values?: Record<string, string | number>,
) => intl.formatMessage(descriptor, values)

export function formatAddedBaggage(addedBaggage: Record<string, number>, intl: IntlShape): Array<App.InclusionFeature> {
  return Object.entries(addedBaggage).map(([description, quantity]) => {
    return {
      description: formatMessage(intl, messages.purchasedBaggage, { quantity, description }),
      type: 'checked_baggage',
    }
  })
}

export function formatPendingBaggage(pendingBaggage: Record<string, number>, intl: IntlShape): Array<App.InclusionFeature> {
  return Object.entries(pendingBaggage).map(([description, quantity]) => {
    return {
      description: formatMessage(intl, messages.pendingBaggage, { quantity, description }),
      type: 'checked_baggage',
    }
  })
}

export function getDefaultNotIncludedText(key: string, intl: IntlShape) {
  switch (key) {
    case 'checkInBags':
    case 'cabinBags':
    case 'meal':
      return formatMessage(intl, messages.notIncluded)
    // A traveller holding no seat still needs the row rendered, otherwise the link into the seat
    // picker has nowhere to sit and that traveller loses their way in entirely.
    case 'seatReservation':
      return formatMessage(intl, messages.seatNotSelected)
    default:
      return undefined
  }
}

export const getFlightIngressIcon = (key: string) => {
  switch (key) {
    case 'seatReservation':
      return LineSeatIcon({ colour: 'light-foreground-alt', size: 'S' })
    case 'cabinBags':
      return LineBagIcon({ colour: 'light-foreground-alt', size: 'S' })
    case 'checkInBags':
      return LineLuggageIcon({ colour: 'light-foreground-alt', size: 'S' })
    case 'meal':
      return LineUtensilsIcon({ colour: 'light-foreground-alt', size: 'S' })
    default:
      return undefined
  }
}

/**
 * Extracts passport details from flights my escapes forms.
 */
export function hasIncompletePassport(traveller: App.FlightTraveller): boolean {
  return !traveller.passport?.number ||
    !traveller.passport.nationality ||
    !traveller.passport.country_of_issue ||
    !traveller.passport.expiry_date
}

export function hasAnyIncompletePassportDetails(
  allFlightDetails: Array<{ flightDetails: App.FlightOrderItemDetails | undefined }>,
): boolean {
  return allFlightDetails.some(({ flightDetails }) => {
    const isCharter = flightDetails?.bookingJourney?.legs?.some(leg => leg.provider === CHARTER) ?? false
    return flightDetails?.travellers.some(t => {
      const passportRequired = isCharter || t.included_passport_details
      return passportRequired && hasIncompletePassport(t)
    })
  })
}

export function extractPassportDetails(formData: Record<string, any>): App.FlightTravellerPassport | undefined {
  const { nationality, passportCountry, passportExpiry, passportNumber } = formData

  if (!nationality || !passportCountry || !passportExpiry || !passportNumber) {
    return
  }

  return {
    nationality,
    number: passportNumber,
    expiry_date: passportExpiry,
    country_of_issue: passportCountry,
  }
}

export function extractEmergencyContact(formData: Record<string, any>): App.FlightEmergencyContact | undefined {
  const {
    emergencyFirstName,
    emergencyLastName,
    emergencyRelationship,
  } = formData

  const emergencyPhone = formData['emergencyPhone.phone']
  const emergencyPhonePrefix = formData['emergencyPhone.prefix']

  if (
    !emergencyFirstName ||
    !emergencyLastName ||
    !emergencyPhone ||
    !emergencyPhonePrefix ||
    !emergencyRelationship
  ) {
    return undefined
  }

  const country = allCountries.find((c) => c.dialCode === emergencyPhonePrefix)

  return {
    firstName: emergencyFirstName,
    lastName: emergencyLastName,
    phone: `${emergencyPhonePrefix}${emergencyPhone}`,
    relationship: emergencyRelationship,
    countryCode: country?.iso2.toUpperCase() ?? '',
  }
}

/**
 * Gets the earliest departure and latest arrival dates from flight details.
 * Uses only the first flight segment of the first leg of each flight item to match
 * the old order.flightItems behavior (which only stored journey.departing.flights[0] dates).
 */
export const getFlightDatesFromDetails = (
  flightItems: Array<App.OrderFlightItem>,
  flightDetailsState: App.OrdersState['flightDetails'],
): { earliest: string | undefined, latest: string | undefined } => {
  const allFlightDetails = getAllFlightDetails(flightItems, flightDetailsState)

  if (allFlightDetails.length === 0) {
    return { earliest: undefined, latest: undefined }
  }

  const flightItemDates = allFlightDetails
    .map(details => {
      const firstLeg = details.bookingJourney?.legs?.[0]
      const firstSegment = firstLeg?.flights?.[0]
      return {
        departure: firstSegment?.departingDate,
        arrival: firstSegment?.arrivalDate,
      }
    })
    .filter(d => d.departure || d.arrival)

  const departureDates = flightItemDates.map(d => d.departure).filter(Boolean).sort()
  const arrivalDates = flightItemDates.map(d => d.arrival).filter(Boolean).sort()

  return {
    earliest: departureDates.length > 0 ? departureDates[0] : undefined,
    latest: arrivalDates.length > 0 ? arrivalDates[arrivalDates.length - 1] : undefined,
  }
}

/**
 * Gets travel durations from non-flight order items.
 */
const getNonFlightTravelDurations = (order: App.Order) => {
  const nonCancelledItems = order.items.filter(item => item.status !== ITEM_STATUS_CANCELLED)
  return nonNullable([
    ...(nonCancelledItems.length > 0 ? nonCancelledItems : order.items).map(item => ({
      startDate: item.reservation?.startDate,
      endDate: item.reservation?.endDate,
    })),
    ...order.bedbankItems.filter(item => item.status !== 'cancelled').map(item => ({
      startDate: item.checkIn.format('YYYY-MM-DD'),
      endDate: item.checkOut.format('YYYY-MM-DD'),
    })),
    ...order.cruiseItems.map(item => ({
      startDate: item.departureDate,
      endDate: item.arrivalDate,
    })),
    ...order.tourItems.map(item => ({
      startDate: item.tour.startDate,
      endDate: item.tour.endDate,
    })),
    ...order.experienceItems.map(item => ({
      startDate: item.date,
      endDate: item.date,
    })),
    ...order.carHireItems.map(item => ({
      startDate: item.reservation?.pickUp.date,
      endDate: item.reservation?.dropOff.date,
    })),
    ...order.customOfferItems.map(item => ({
      startDate: item.custom_offer.items[0]?.start_date,
      endDate: item.custom_offer.items[0]?.end_date,
    })),
  ].filter(item => item.startDate || item.endDate))
}

/**
 * Gets the order travel duration using flight details for accurate flight dates.
 * For bundled orders, combines flight dates from flightDetails with other item dates from order.
 * Returns undefined dates if flight items exist but flight details haven't been fetched yet.
 */
export const getOrderTravelDurationWithFlightDetails = (
  order: App.Order,
  flightDetailsState: App.OrdersState['flightDetails'],
): { earliest: string | undefined, latest: string | undefined } => {
  const hasFlightItems = order.flightItems.length > 0

  // Get flight dates from flight details (accurate after date changes)
  let flightDates: { earliest: string | undefined, latest: string | undefined } = { earliest: undefined, latest: undefined }
  if (hasFlightItems) {
    flightDates = getFlightDatesFromDetails(order.flightItems, flightDetailsState)

    if (!flightDates.earliest || !flightDates.latest) {
      return { earliest: undefined, latest: undefined }
    }
  }

  const nonFlightDates = getNonFlightTravelDurations(order)

  const allStartDates: Array<string> = []
  const allEndDates: Array<string> = []

  nonFlightDates.forEach(d => {
    if (d.startDate) allStartDates.push(d.startDate)
    if (d.endDate) allEndDates.push(d.endDate)
  })

  if (hasFlightItems && flightDates.earliest && flightDates.latest) {
    allStartDates.push(flightDates.earliest)
    allEndDates.push(flightDates.latest)
  }

  const sortedStartDates = allStartDates.sort()
  const sortedEndDates = allEndDates.sort()

  return {
    earliest: sortedStartDates.length > 0 ? sortedStartDates[0] : undefined,
    latest: sortedEndDates.length > 0 ? sortedEndDates[sortedEndDates.length - 1] : undefined,
  }
}

/**
 * Extracts country code from phone number.
 * Removes NANP matches to avoid getting area codes within the +1 region.
 */
export function extractPhoneNumber(phone: string) {
  const dialCodes = allCountries.map((c) => c.dialCode)
    .filter((c) => c.length <= 3) // remove NANP matches
    .sort((a, b) => Number(b) - Number(a)) //

  for (const countryCode of dialCodes) {
    if (phone.startsWith(countryCode)) {
      return {
        countryCode,
        number: phone.slice(countryCode.length),
      }
    }
  }

  return {
    countryCode: null,
    number: phone,
  }
}

/**
 * The id a post-booking seat map is keyed by, as `{marketingAirline}-{flightNumber}` (e.g. L0-957).
 *
 * Deliberately not the flight's own `id`. That is the order's flight uuid, which svc-flights never
 * sees on this path: it builds the seat map, and matches the flight of a seat addon, off this
 * carrier-and-number form. Sending the uuid instead matches nothing on the map, so the seat change
 * silently fails to apply.
 */
export function seatMapFlightId(flight: {
  flightNumber?: string
  marketingAirline?: { code?: string }
}): string | undefined {
  const carrier = flight.marketingAirline?.code
  if (!carrier || !flight.flightNumber) { return undefined }

  return `${carrier}-${flight.flightNumber}`
}
